import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import useServerPagination from '@/hooks/useServerPagination';
import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';
import { getPDF } from '@/utils/common';

import type { IFilters } from './Filters';
import type { IResOrderById, IResOrders } from '@/types/fulfill/statusOrders';

import { convertArrayToQueryString } from './utils';

interface Order {
  data: IResOrders['orders'];
  loading: boolean;
  error: boolean;
  totalRows: number;
}

interface OrderDetail {
  loading: boolean;
  error: any;
  data: null | IResOrderById['data'];
}

export interface DownloadOrdersProps {
  filters: string;
  report_type: string;
  'filters[createdAtStart]'?: string;
  'filters[createdAtEnd]'?: string;
}

const initialOrdersState: Order = {
  data: [],
  loading: false,
  error: false,
  totalRows: 0,
};

const initialFilters: IFilters = {
  fulfillmentStatus: [],
  deliveryType: [],
};

const initialOrderDetails: OrderDetail = {
  loading: false,
  error: null,
  data: null,
};

const useOrderStatus = () => {
  // TODO: Aplicar refactor de estados a reducer independiente.
  const pagination = useServerPagination();
  const http = initializeHttpProvider();

  const [search, setSearch] = useState('');
  const [openDrawer, setOpenDrawer] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openDownloadModal, setOpenDownloadModal] = useState(false);
  const [openDetailModal, setOpenDetailModal] = useState(false);

  const [orderIdToCancel, setOrderIdToCancel] = useState('');
  const [orders, setOrders] = useState<Order>(initialOrdersState);
  const [orderDetails, setOrderDetails] = useState<OrderDetail>(initialOrderDetails);
  const [filters, setFilters] = useState<IFilters>(initialFilters);

  const getOrders = useCallback(
    async ({ deliveryType, fulfillmentStatus }: IFilters) => {
      setOrders((prev) => ({ ...prev, loading: true }));

      try {
        const apiParams = {
          deliveryType: convertArrayToQueryString(deliveryType!, 'deliveryType'),
          fulfillmentStatus: convertArrayToQueryString(fulfillmentStatus!, 'fulfillmentStatus'),
          page: (pagination.filters.page ?? 0) + 1,
          page_size: pagination.filters.page_size,
          search: search || undefined,
        };

        const response = await http.statusOrder.orders(apiParams);

        setOrders({
          data: response.orders,
          loading: false,
          error: false,
          totalRows: response.metadata.total_records,
        });
      } catch (err) {
        console.error(err);
        setOrders((prev) => ({ ...prev, loading: false, error: true }));
      }
    },
    [pagination.filters.page, pagination.filters.page_size, search],
  );

  useEffect(() => {
    void getOrders(filters);
  }, [getOrders, filters]);

  const cancelOrder = async () => {
    toast.promise(
      async () => {
        await http.statusOrder.cancelOrder({
          orderId: orderIdToCancel,
          fulfillmentStatus: 'canceled',
        });
        await getOrders(filters);
      },
      {
        pending: 'Cancelando orden...',
        success: 'Orden cancelada correctamente.',
        error: 'Ha ocurrido un error al intentar cancelar la orden.',
      },
    );
    setOpenModal(false);
  };

  const fetchOrderByTrackingId = useCallback(async (trackingId: string) => {
    setOpenDetailModal(true);
    setOrderDetails((prev) => ({ ...prev, loading: true, data: null }));
    try {
      const response = await http.statusOrder.orderById({ trackingId });
      setOrderDetails({ loading: false, error: null, data: response.data });
    } catch (error) {
      setOrderDetails((prev) => ({ ...prev, loading: false, error }));
    }
  }, []);

  const downloadOrders = async (params: DownloadOrdersProps) => {
    toast.promise(
      async () => {
        const response = await http.statusOrder.downloadOrders(params);
        if (!response.file_url) throw new Error('Error al descargar el reporte');
        window.open(response.file_url, '_blank');
      },
      {
        pending: 'Descargando reporte...',
        success: 'Descarga exitosa',
        error: 'Ha ocurrido un error al intentar descargar el reporte',
      },
    );
    setOpenDownloadModal(false);
  };

  const downloadOrderLabel = async (orderId: string) => {
    toast.promise(
      async () => {
        const response = await http.statusOrder.downloadOrderLabel({ orderId });
        if (!response.data) throw new Error('Error al descargar la etiqueta');
        getPDF(response.data);
      },
      {
        pending: 'Descargando etiqueta...',
        success: 'Descarga exitosa',
        error: 'Ha ocurrido un error al intentar descargar la etiqueta',
      },
    );
  };

  const clearFilters = useCallback(async () => {
    setFilters(initialFilters);
    await getOrders(initialFilters);
    setOpenDrawer(false);
  }, [getOrders]);

  const applyFilters = useCallback(() => {
    void getOrders(filters);
    setOpenDrawer(false);
  }, [filters, getOrders]);

  const onChangeFilter = useCallback((filterName: keyof IFilters, value: string) => {
    setFilters((prev) => {
      const currentValues = (prev[filterName] as string[]) || [];
      const newValues = currentValues?.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];
      return { ...prev, [filterName]: newValues };
    });
  }, []);

  const fetchOrdersWithFilters = useCallback(() => {
    void getOrders(filters);
  }, [filters, getOrders]);

  const onChgSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;

      if (value.length >= 5) {
        setSearch(value);
      }
      if (value.length === 0) {
        setSearch('');
        void fetchOrdersWithFilters();
      }
    },
    [fetchOrdersWithFilters],
  );

  const onSearch = useCallback(() => {
    void fetchOrdersWithFilters();
    pagination.onChangeFilter({ page: 0 });
  }, [fetchOrdersWithFilters, pagination]);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSearch();
    }
  };

  const hasActiveFilters = useMemo(() => {
    return (
      (filters.fulfillmentStatus ?? []).length > 0 ||
      (filters.deliveryType ?? []).length > 0 ||
      search.length >= 5
    );
  }, [filters, search]);

  useEffect(() => {
    void getOrders(filters);
  }, [pagination.filters]);

  return {
    openDrawer,
    handleOpenDrawer: () => setOpenDrawer((prev) => !prev),
    orders,
    clearFilters,
    filters,
    onChangeFilter,
    applyFilters,
    onChgSearch,
    handleKeyPress,
    onSearch,
    cancelOrder,
    search,
    handleOpenModal: (orderId: string) => {
      setOrderIdToCancel(orderId);
      setOpenModal(true);
    },
    openModal,
    handleCloseModal: () => setOpenModal(false),
    openDetailModal,
    handleCloseDetailModal: () => {
      setOpenDetailModal(false);
      setOrderDetails(initialOrderDetails);
    },
    orderDetails,
    fetchOrderByTrackingId,
    pagination,
    openDownloadModal,
    handleOpenDownloadModal: () => setOpenDownloadModal(true),
    handleCloseDownloadModal: () => setOpenDownloadModal(false),
    downloadOrders,
    downloadOrderLabel,
    hasActiveFilters,
  };
};

export default useOrderStatus;
